import React, { useEffect, useRef, useState } from "react";
import {
  CreatableSelect,
  CreatableOption,
} from "@/components/ui/creatable-select";
import ReactPlayer from "react-player";
import { SkedIcon, TooltipWrapper } from "ui";
import { ExternalLink } from "ui/Link";
import { useDispatch, useSelector } from "react-redux";
import { hideModal } from "actions/modal";
import { updateCollectionAsset } from "actions/mediaLibrary";
import { getTags } from "selectors/mediaLibrarySelectors";
import bytes from "bytes";
import map from "lodash/map";
import isEmpty from "lodash/isEmpty";
import filter from "lodash/filter";
import uniqBy from "lodash/uniqBy";
import initial from "lodash/initial";
import { isLibraryRestricted } from "features/media-library";
import { getUser } from "selectors/commonSelectors";
import { TextArea, TextInput } from "ui/ecosystems/forms/inputs";
import { useSkedCaptionAutocomplete } from "ui/SkedCaption";
import { checkIfHeic } from "utils/image";
import { Icon } from "@dialogueconsulting/sked-ui";
import { colors } from "styles/color-tokens";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { CollectionFile } from "shared/types/files";
import { UserWithPlan } from "@/features/media-library/is-library-restricted";
import { convertCustomerUploadsS3ToCloudFront } from "../../../libs/media-files";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { SkedCaptionV2 } from "../../../ui/SkedCaption/v2/sked-caption-v2";

const getAssetImage = (asset: CollectionFile, file: File | null): string => {
  let imageUrl = asset.thumbnail ?? "";
  const isImage = asset.meta?.isImage;

  if (isImage) {
    const source = file || asset;
    // Type guard: File objects don't have meta property, only CollectionFile does
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isCollectionFile = (obj: any): obj is CollectionFile => "meta" in obj;
    const sourceAsset = isCollectionFile(source) ? source : asset;
    const isHeic = checkIfHeic(sourceAsset.meta?.mime) && sourceAsset.thumbnail;

    imageUrl = isHeic ? sourceAsset.thumbnail : (sourceAsset.downloadUrl ?? "");
  }

  return imageUrl;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const buildTags = (tagObjs: any) => {
  const tags: string[] = [];
  for (const tagObj of tagObjs) {
    if (!isEmpty(tagObj.value)) {
      tags.push(tagObj.value);
    }
  }
  return tags;
};

const EditCollectionAssetModal = ({
  asset,
  isReadOnly,
  loading,
  btnCancel = "Cancel",
}: {
  asset: CollectionFile;
  isReadOnly: boolean;
  loading: boolean;
  btnCancel: string;
}) => {
  const dispatch = useDispatch();

  const formattedTags: CreatableOption[] = [];
  map(asset.tags, (tag) => {
    if (isEmpty(tag)) {
      return;
    }
    formattedTags.push({
      label: tag,
      value: tag,
    });
  });

  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState(asset.name);
  const [tags, setTags] = useState<CreatableOption[]>(formattedTags);
  const [suggestedTags, setSuggestedTags] = useState<
    { value: string; selected: boolean }[] | null
  >(null);
  const [description, setDescription] = useState(asset.description);
  const [source] = useState(asset.source.name);
  const [sourceNotes, setSourceNotes] = useState(asset.source.notes);
  const [sourceValue] = useState(asset.source.value);
  const allTags = useSelector(getTags);

  // Track previous file preview for cleanup
  const previousPreviewRef = useRef<string | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const buildSuggestedTags = (tagObjs: any) => {
    const tempTags: { value: string; selected: boolean }[] = [];
    for (const tagObj of tagObjs) {
      const matching = filter(tags, (tag) => tag.value === tagObj.description);
      tempTags.push({
        value: tagObj.description,
        selected: matching.length > 0,
      });
    }

    return uniqBy(tempTags, "value");
  };

  useEffect(() => {
    if (asset.meta?.google?.labels) {
      setSuggestedTags(buildSuggestedTags(asset.meta.google.labels));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tags]);

  // Cleanup when file changes or on unmount
  useEffect(() => {
    // Revoke previous preview if it exists and is different
    if (
      previousPreviewRef.current &&
      previousPreviewRef.current !== (file as any)?.preview
    ) {
      URL.revokeObjectURL(previousPreviewRef.current);
    }

    // Track current preview
    previousPreviewRef.current = (file as any)?.preview || null;

    // Cleanup on unmount
    return () => {
      if (previousPreviewRef.current) {
        URL.revokeObjectURL(previousPreviewRef.current);
      }
    };
  }, [file]);

  const handleFileBrowse = (
    e: React.ChangeEvent<HTMLInputElement>,
    ref: React.RefObject<HTMLInputElement>
  ) => {
    e.stopPropagation();
    e.preventDefault();
    const fileMap = ref.current?.files;
    if (fileMap?.length === 0) {
      return;
    }

    const newFile = fileMap?.[0];

    if (!newFile) {
      return;
    }

    Object.assign(newFile, {
      preview: URL.createObjectURL(newFile),
    });

    setFile(newFile);
  };

  const handleSaveAsset = () => {
    const formattedTags = buildTags(tags);
    dispatch(
      updateCollectionAsset(
        asset,
        file,
        fileName,
        description,
        formattedTags,
        source,
        sourceNotes,
        asset.favorite // keep the current favorite status
      )
    );
  };

  const addSuggestedTag = (tag: string) => {
    const existingTags = isEmpty(tags) ? [] : tags;
    setTags([...existingTags, { label: tag, value: tag }]);
  };

  const handleClose = () => {
    dispatch(hideModal());
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        className={cn("p-0", isReadOnly ? "max-w-[1000px]" : "max-w-[900px]")}
        showCloseButton={false}
      >
        <DialogHeader className="px-6 py-3 border-b border-neutral-200">
          <DialogTitle className="font-gotham text-base font-semibold text-neutral-900">
            {isReadOnly ? "Asset details" : "Edit Asset"}
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="px-6 py-4 max-h-[60vh]">
          {asset && (
            <AssetDetail
              isReadOnly={isReadOnly}
              asset={asset}
              file={file}
              fileName={fileName}
              setFileName={setFileName}
              allTags={allTags}
              tags={tags}
              setTags={setTags}
              description={description}
              setDescription={setDescription}
              handleFileBrowse={handleFileBrowse}
              suggestedTags={suggestedTags}
              addSuggestedTag={addSuggestedTag}
              source={source}
              sourceNotes={sourceNotes}
              setSourceNotes={setSourceNotes}
              sourceValue={sourceValue ?? ""}
            />
          )}
        </DialogBody>

        <DialogFooter className="flex flex-row items-center justify-between sm:justify-between border-t border-neutral-200 px-6 py-3">
          {isReadOnly ? (
            <>
              <div />
              <Button variant="blue-primary" onClick={handleClose}>
                Close
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                disabled={loading}
                onClick={handleClose}
                className="text-neutral-600 hover:text-neutral-700 hover:bg-neutral-100"
              >
                {btnCancel}
              </Button>
              <Button
                variant="blue-primary"
                disabled={loading}
                onClick={handleSaveAsset}
              >
                {loading ? (
                  <>
                    <Spinner size="sm" variant="inverted" className="mr-2" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AssetDetail = ({
  asset,
  file,
  fileName,
  setFileName,
  allTags,
  tags,
  setTags,
  description,
  setDescription,
  handleFileBrowse,
  suggestedTags,
  addSuggestedTag,
  source,
  sourceValue,
  sourceNotes,
  setSourceNotes,
  isReadOnly,
}: {
  asset: CollectionFile;
  file: File | null;
  fileName: string;
  setFileName: (fileName: string) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  allTags: any[];
  tags: { label: string; value: string }[];
  setTags: (tags: { label: string; value: string }[]) => void;
  description: string;
  setDescription: (description: string) => void;
  handleFileBrowse: (
    e: React.ChangeEvent<HTMLInputElement>,
    ref: React.RefObject<HTMLInputElement>
  ) => void;
  suggestedTags: { value: string; selected: boolean }[] | null;
  addSuggestedTag: (tag: string) => void;
  source: string;
  sourceValue: string;
  sourceNotes: string;
  setSourceNotes: (sourceNotes: string) => void;
  isReadOnly: boolean;
}) => {
  const user = useSelector(getUser);
  const isVideo = asset.meta?.isVideo;

  const skedCaptionAutocomplete = useSkedCaptionAutocomplete();

  const fileBrowseRef = useRef(null);

  const fileNameParts = fileName.split(".");
  let fileType = null;
  let fileNameString = fileName;
  if (fileNameParts.length > 1) {
    fileType = fileNameParts.at(-1);
    fileNameString = initial(fileNameParts).join("."); // remove filetype
  }

  const handleClickSuggested = (suggestedTag: {
    value: string;
    selected: boolean;
  }) => {
    if (suggestedTag.selected) {
      return;
    }

    addSuggestedTag(suggestedTag.value);
  };

  const imageUrl = getAssetImage(asset, file);
  const downloadUrl = asset.downloadUrl ?? "";

  const size = asset.meta ? Number.parseInt(asset.meta.size.toString()) : 0;

  return (
    <div className="flex gap-8">
      {/* Left side - Image preview */}
      <div className="w-1/2 flex flex-col">
        <div className="flex flex-col justify-center rounded-lg overflow-hidden bg-neutral-50">
          {!isVideo && (
            <a
              href={convertCustomerUploadsS3ToCloudFront(downloadUrl)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex justify-center"
            >
              <img
                alt="Preview"
                data-testid="assetPreview"
                className="object-contain object-center max-w-full max-h-[400px]"
                src={convertCustomerUploadsS3ToCloudFront(imageUrl)}
              />
            </a>
          )}
          {isVideo && (
            <ReactPlayer
              src={convertCustomerUploadsS3ToCloudFront(downloadUrl)}
              controls
            />
          )}
          <input
            type="file"
            name="fileInput"
            accept=".png, .jpg, .jpeg, .tiff, .bmp, .mp4, .gif, .heic, .heif, .pdf"
            style={{ display: "none" }}
            ref={fileBrowseRef}
            onChange={(e) => handleFileBrowse(e, fileBrowseRef)}
          />
        </div>
      </div>

      {/* Right side - Form fields */}
      <div className="w-1/2 space-y-4">
        {/* File name */}
        <div className="space-y-1.5">
          <label className="font-gotham text-sm font-semibold text-neutral-700">
            File name
          </label>
          {isReadOnly ? (
            <p className="font-inter text-sm text-neutral-900">
              {`${fileNameString}.${fileType}`}
            </p>
          ) : (
            <div className="flex items-center gap-2">
              <TextInput
                value={fileNameString}
                placeholder="Enter filename"
                onChange={(value: string) =>
                  setFileName(`${value}.${fileType}`)
                }
                className="flex-1"
              />
              {fileType && (
                <span
                  className="font-inter text-sm text-neutral-500 truncate w-20"
                  data-testid="assetFileType"
                >
                  .{fileType}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Description */}
        <div className="space-y-1.5">
          <label className="font-gotham text-sm font-semibold text-neutral-700">
            Description
          </label>
          {isReadOnly ? (
            <p className="font-inter text-sm text-neutral-900">
              {description || (
                <span className="text-neutral-400">No description</span>
              )}
            </p>
          ) : (
            <SkedCaptionV2
              initialValue={description}
              descriptionsHidden
              autocomplete={skedCaptionAutocomplete}
              onChange={(e: string) => setDescription(e)}
            />
          )}
        </div>

        {/* Tags */}
        <div className="space-y-1.5">
          <label className="font-gotham text-sm font-semibold text-neutral-700 flex items-center gap-2">
            Tags
            {isReadOnly && (
              <TooltipWrapper tooltip="These are tags synced through Frontify and can only be added or removed there.">
                <Icon name="info-circle" size="sm" color={colors.neutral500} />
              </TooltipWrapper>
            )}
          </label>
          {isLibraryRestricted(user as UserWithPlan) ? (
            <TooltipWrapper tooltip="To use tagging, please upgrade your library.">
              <CreatableSelect
                options={[]}
                value={tags}
                onChange={setTags}
                className="w-full"
                placeholder="Tags to easily find your asset later"
                disabled={true}
              />
            </TooltipWrapper>
          ) : (
            <div>
              {isReadOnly ? (
                <div className="flex flex-wrap gap-1.5">
                  {(tags || []).length > 0 ? (
                    tags.map((tag) => (
                      <span
                        key={tag.value}
                        className="inline-flex items-center px-2 py-0.5 rounded bg-neutral-100 text-neutral-700 font-inter text-sm"
                      >
                        {tag.value}
                      </span>
                    ))
                  ) : (
                    <span className="font-inter text-sm text-neutral-400">
                      No tags
                    </span>
                  )}
                </div>
              ) : (
                <CreatableSelect
                  options={allTags}
                  value={tags}
                  onChange={setTags}
                  className="w-full"
                  placeholder="Tags to easily find your asset later"
                  createLabel='Create tag "{search}"'
                />
              )}
            </div>
          )}
        </div>

        {/* Suggested Tags */}
        {!isLibraryRestricted(user as UserWithPlan) &&
          suggestedTags &&
          suggestedTags.length > 0 && (
            <div className="space-y-1.5">
              <label className="font-gotham text-sm font-semibold text-neutral-700">
                Suggested Tags
              </label>
              <div className="flex flex-wrap gap-1.5">
                {suggestedTags.map((suggestedTag, index) => (
                  <button
                    key={`${suggestedTag.value}-${index}`}
                    type="button"
                    className={cn(
                      "inline-flex items-center px-2 py-0.5 rounded text-xs font-medium transition-colors",
                      suggestedTag.selected
                        ? "bg-green-100 text-green-700 cursor-default"
                        : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200 cursor-pointer"
                    )}
                    onClick={() => handleClickSuggested(suggestedTag)}
                  >
                    {suggestedTag.value}
                  </button>
                ))}
              </div>
            </div>
          )}

        {/* Metadata section */}
        {asset.meta?.width && asset.meta?.height && asset.meta?.size && (
          <div className="pt-4 border-t border-neutral-100 space-y-2">
            {source && (
              <div className="flex items-center gap-2">
                <span className="font-gotham text-sm font-semibold text-neutral-700 w-24">
                  Source
                </span>
                <span className="font-inter text-sm text-neutral-600">
                  {sourceValue && sourceValue.includes("http") ? (
                    <ExternalLink
                      href={sourceValue}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:text-blue-600"
                    >
                      {source}
                    </ExternalLink>
                  ) : (
                    source
                  )}
                </span>
              </div>
            )}

            {sourceNotes && (
              <div className="space-y-1.5">
                <label className="font-gotham text-sm font-semibold text-neutral-700 flex items-center gap-2">
                  Source notes
                  <SkedIcon
                    className="inline-block"
                    icon="icon-help"
                    size="12"
                    tooltip={`Notes included when the user added this asset to your library.`}
                    placement="top"
                  />
                </label>
                <TextArea
                  value={sourceNotes}
                  className="leading-normal min-h-[80px] p-3"
                  onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setSourceNotes(event.target.value)
                  }
                />
              </div>
            )}

            <div className="flex items-center gap-2">
              <span className="font-gotham text-sm font-semibold text-neutral-700 w-24">
                Dimensions
              </span>
              <span className="font-inter text-sm text-neutral-600">
                {asset.meta.width}x{asset.meta.height}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <span className="font-gotham text-sm font-semibold text-neutral-700 w-24">
                Size
              </span>
              <span className="font-inter text-sm text-neutral-600">
                {bytes(size, { decimalPlaces: 0 })}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditCollectionAssetModal;
